// One conversation — the transcript, a live reply streaming in, and the composer.
//
// A reply has two sources that race, on purpose. Tokens arrive over the run's
// Ably channel and are shown as they come. The saved message is completed on
// the server from the run's audit row, and this page polls the thread while a
// reply is pending. Whichever lands first wins; the saved text is what stays.
// So a reply still appears when Ably isn't configured, when the run finishes
// before the browser subscribes, or when the tab was closed mid-answer.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowUp, Bot, Check, Copy } from "lucide-react";
import * as React from "react";

import { AnswerRatingControls } from "@/components/chat/answer-rating";
import { AccuracyChip } from "@/components/evaluations/accuracy";
import { Markdown } from "@/components/chat/markdown";
import { Button, Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { getConversation, sendChatMessage } from "@/lib/api/chat";
import { isStubHandoff, subscribeRun } from "@/lib/api/governed-stream";
import { useParams } from "@/lib/navigation";
import { qk } from "@/lib/query/keys";
import { cn } from "@/lib/utils";
import type { ChatConversationDetail, ChatMessage } from "@/types/api";

export default function ConversationPage() {
  const id = String(useParams().id);
  const qc = useQueryClient();
  const conv = useQuery({
    queryKey: qk.chatConversation(id),
    queryFn: () => getConversation(id),
    // Fallback for the stream: keep reading while a reply is pending, and
    // while a fresh answer's accuracy badge is still on its way.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d?.messages.some((m) => m.status === "pending")) return 2500;
      return d?.accuracy_scoring && awaitingBadge(d.messages) ? 5000 : false;
    },
  });
  const [live, setLive] = React.useState<{ runId: string; text: string } | null>(null);
  const unsubscribe = React.useRef<(() => void) | null>(null);

  React.useEffect(
    () => () => {
      unsubscribe.current?.();
      unsubscribe.current = null;
      setLive(null);
    },
    [id],
  );

  const refresh = React.useCallback(async () => {
    await qc.invalidateQueries({ queryKey: qk.chatConversation(id) });
    await qc.invalidateQueries({ queryKey: qk.chatConversations });
  }, [qc, id]);

  const send = useMutation({
    mutationFn: (content: string) => sendChatMessage(id, content),
    onSuccess: (res) => {
      qc.setQueryData<ChatConversationDetail>(qk.chatConversation(id), (old) =>
        old
          ? { ...old, messages: [...old.messages, res.user_message, res.assistant_message] }
          : old,
      );
      void qc.invalidateQueries({ queryKey: qk.chatConversations });
      if (isStubHandoff(res)) return; // no Ably here — the poll will pick it up
      setLive({ runId: res.run_id, text: "" });
      unsubscribe.current?.();
      unsubscribe.current = subscribeRun(res, {
        onToken: (t) =>
          setLive((l) => (l && l.runId === res.run_id ? { ...l, text: l.text + t } : l)),
        onEvent: (kind) => {
          // A validator sent the model back for another attempt: the text so
          // far is being replaced, not continued.
          if (kind === "validation_rejected") setLive({ runId: res.run_id, text: "" });
        },
        onDone: () => {
          unsubscribe.current = null;
          void refresh().then(() => setLive(null));
        },
        onError: () => {
          unsubscribe.current = null;
          setLive(null);
          void refresh();
        },
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (conv.isError) {
    return (
      <div className="p-8">
        <ErrorState error={conv.error} onRetry={() => conv.refetch()} />
      </div>
    );
  }
  if (conv.isLoading || !conv.data) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-4 p-8">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="ml-auto h-10 w-1/2" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const data = conv.data;
  const busy = send.isPending || data.messages.some((m) => m.status === "pending");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <Bot className="h-4 w-4 text-primary" />
        <span className="font-medium">{data.agent_name}</span>
        <span className="truncate text-sm text-muted-foreground">· {data.title}</span>
        {data.is_evaluation ? (
          <span className="ml-auto shrink-0 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
            Evaluation session
          </span>
        ) : null}
      </header>
      <Transcript
        conversationId={data.id}
        messages={data.messages}
        live={live}
        agentName={data.agent_name}
      />
      <Composer
        disabled={busy || !data.agent_available}
        notice={
          !data.agent_available
            ? "This agent is no longer available. You can read this chat, or start a new one."
            : null
        }
        onSend={(text) => send.mutate(text)}
      />
    </div>
  );
}

function Transcript({
  conversationId,
  messages,
  live,
  agentName,
}: {
  conversationId: string;
  messages: ChatMessage[];
  live: { runId: string; text: string } | null;
  agentName: string;
}) {
  const end = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [messages.length, live?.text]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
        {messages.length === 0 ? (
          <p className="pt-16 text-center text-sm text-muted-foreground">
            Say hello to {agentName}.
          </p>
        ) : null}
        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-muted px-4 py-2.5 text-sm">
                {m.content}
              </div>
            </div>
          ) : (
            <AssistantMessage
              key={m.id}
              conversationId={conversationId}
              message={m}
              liveText={live && m.run_id === live.runId ? live.text : null}
            />
          ),
        )}
        <div ref={end} />
      </div>
    </div>
  );
}

function AssistantMessage({
  conversationId,
  message,
  liveText,
}: {
  conversationId: string;
  message: ChatMessage;
  liveText: string | null;
}) {
  const [copied, setCopied] = React.useState(false);
  const pending = message.status === "pending";
  const text = pending ? (liveText ?? "") : message.content;

  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bot className="h-4 w-4" />
      </span>
      <div className="group min-w-0 flex-1">
        {message.status === "failed" ? (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {message.content}
          </p>
        ) : pending && !text ? (
          <TypingDots />
        ) : (
          <Markdown text={text} />
        )}
        {message.status === "complete" && message.content ? (
          <div className="mt-1 flex items-center gap-0.5">
            <button
              type="button"
              className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
              aria-label="Copy reply"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(message.content);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {
                  toast.error("Copy failed");
                }
              }}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            {message.run_id ? (
              <AnswerRatingControls
                conversationId={conversationId}
                messageId={message.id}
                current={message.my_rating}
              />
            ) : null}
            {message.accuracy ? (
              <span
                className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground"
                title={
                  message.accuracy_source === "evaluator"
                    ? "Accuracy set by an evaluator's rating"
                    : "Automatic accuracy score"
                }
              >
                Accuracy
                <AccuracyChip level={message.accuracy} pct={message.accuracy_pct} />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** How long after an answer its badge is worth waiting for. */
const BADGE_WAIT_MS = 2 * 60 * 1000;

function awaitingBadge(messages: ChatMessage[]) {
  const last = [...messages].reverse().find((m) => m.role === "assistant");
  return (
    !!last &&
    last.status === "complete" &&
    !!last.run_id &&
    !last.accuracy &&
    Date.now() - new Date(last.created_at).getTime() < BADGE_WAIT_MS
  );
}

function TypingDots() {
  return (
    <div className="flex h-6 items-center gap-1" aria-label="Thinking">
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground"
          style={{ animationDelay: `${d}ms` }}
        />
      ))}
    </div>
  );
}

function Composer({
  disabled,
  notice,
  onSend,
}: {
  disabled: boolean;
  notice: string | null;
  onSend: (text: string) => void;
}) {
  const [text, setText] = React.useState("");
  const ref = React.useRef<HTMLTextAreaElement>(null);

  // Grow with the content up to a cap, like every chat box people know.
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  const submit = () => {
    const value = text.trim();
    if (!value || disabled) return;
    onSend(value);
    setText("");
  };

  return (
    <div className="shrink-0 px-4 pb-4">
      <div className="mx-auto w-full max-w-3xl">
        {notice ? <p className="mb-2 text-center text-xs text-muted-foreground">{notice}</p> : null}
        <form
          className={cn(
            "flex items-end gap-2 rounded-2xl border border-input bg-card px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-ring",
          )}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <textarea
            ref={ref}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Message"
            aria-label="Message"
            className="max-h-[200px] flex-1 resize-none bg-transparent py-1.5 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Button
            type="submit"
            size="icon"
            className="h-8 w-8 rounded-full"
            disabled={disabled || !text.trim()}
            aria-label="Send"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </form>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Agents can make mistakes. Check important information.
        </p>
      </div>
    </div>
  );
}
